import React, { useMemo } from 'react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import cn from '@/utils/cn';
import I18n from '@/modules/i18n';
import Text from '@/components/ui/Text';
import Tooltip from '@/components/ui/Tooltip';
import { IStudent } from '@/components/staffer/AssignmentsBasedGradesTable/types';
import useGradesTableStore from '@/components/staffer/AssignmentsBasedGradesTable/store/useGradesTableStore';
import { StatusIndicatorIcon } from '@/components/staffer/AssignmentsBasedGradesTable/Table/Rows/StatusIndicator';
import IStoreSubmission from '@/components/interfaces/Submission';
import { ISubmission } from '@/components/staffer/Grading/types';
import { normalizedScore } from '@/components/staffer/Grading/utils';
import { getColumnName } from './getColumnName';
import { useProgresses } from './useProgresses';
import { useAssignedItems } from './useAssignedItems';
import { ReadingStatusIndicator } from '../ReadingStatusIndicator';
import { IExerciseProgress } from './ReadingAssignmentsTable.types';
import ReadingStatusLink from '../ReadingStatusLink';

type OverallProgress = 'completed' | 'in_progress' | undefined;
const getOverallProgress = (submission?: Pick<IStoreSubmission, 'in_progress' | 'submitted_at'>): OverallProgress => {
  if (submission?.submitted_at) return 'completed';
  if (submission?.in_progress) return 'in_progress';

  return undefined;
};

const getDisplayScore = (submission: ISubmission, progressPoints: number) => {
  if (typeof submission?.total_score_override === 'number') return submission.total_score_override;
  if (typeof submission?.normalized_total_score === 'number') return submission.normalized_total_score;
  if (typeof submission?.total_score === 'number') return submission.total_score;

  return progressPoints;
};

type TableRow = IStudent & { progresses: IExerciseProgress[] };

const columnHelper = createColumnHelper<TableRow>();

export function useReadingAssignmentTable() {
  const assignedItems = useAssignedItems();
  const allAssignmentsAssigneeModels = useGradesTableStore(state => state.assignmentsAssigneeModels);
  const assignment = useGradesTableStore(state => state.assignment);
  const submissions = useGradesTableStore(state => state.submissions);
  const students = useGradesTableStore(state => state.students);
  const isQuestionScoringMode = assignment.scoring_mode === 'question';

  const { data: notebookData } = useProgresses('Notebook');
  const { data: readingGameData } = useProgresses('ReadingGame');
  const exerciseData = [...notebookData, ...readingGameData];

  const tableData = students.map((student) => {
    const progresses = exerciseData.filter(datum => datum.student_id === student.id);

    return { ...student, progresses };
  });

  const fullNameCol = columnHelper.display({
    cell: ({ row: { original: { first_name, last_name } } }) => (
      <Tooltip.Provider>
        <Tooltip>
          <Tooltip.Portal>
            <Tooltip.Content
              className="tw-z-9999"
              theme="white"
            >
              {`${first_name} ${last_name}`}
              <Tooltip.Arrow />
            </Tooltip.Content>
          </Tooltip.Portal>
          <Tooltip.Trigger asChild>
            <div>
              <Text
                className={cn(
                  'tw-text-ellipsis tw-overflow-hidden tw-block',
                  'tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-px-3 tw-py-3',
                  'tw-box-border tw-border-r-2 tw-border-solid tw-border-tci-black/15 tw-h-full tw-w-full',
                )}
                size="sm"
                tag="div"
              >
                {`${first_name} ${last_name}`}
              </Text>
            </div>
          </Tooltip.Trigger>
        </Tooltip>
      </Tooltip.Provider>
    ),
    id: 'full_name',
  });

  const totalCol = columnHelper.display({
    cell: ({ row: { original: { id, first_name, last_name, progresses } } }) => {
      const isAssigned = allAssignmentsAssigneeModels.some((aam) => {
        if (aam.assignment_id !== assignment?.id) return false;
        if (aam.model_type === 'Student') return aam.model_id === id;

        return true;
      });

      if (!isAssigned) {
        return (
          <div className="tw-h-full tw-w-full tw-flex tw-justify-center tw-items-center tw-gap-4">
            <StatusIndicatorIcon type="notAssigned" />
          </div>
        );
      }

      const submission = submissions.find(s => (
        s.user_id === id
        && s.user_type === 'Student'
        && s.assignment_id === assignment.id
      ));
      const overallProgressStatus = getOverallProgress(submission);
      const overallProgress = overallProgressStatus ? { ...progresses[0], status: overallProgressStatus } : undefined;
      const progressPoints = Math.round(
        progresses.reduce((sum, p) => (typeof p.points === 'number' ? sum + p.points : sum), 0) * 10,
      ) / 10;
      const hasPoints = typeof submission?.total_score_override === 'number'
                        || progresses.some(p => typeof p.points === 'number');
      const totalPoints = getDisplayScore(submission, progressPoints);

      return (
        <ReadingStatusLink
          progress={overallProgress}
          sectionTitle={I18n.t('total')}
          studentId={id}
          studentName={`${first_name} ${last_name}`}
        >
          {overallProgress && <ReadingStatusIndicator progress={overallProgress} />}
          {isQuestionScoringMode ? (
            <span className="-tw-mt-0.5 tw-text-tci-black">
              {hasPoints ? totalPoints : '-'}
            </span>
          ) : null}
        </ReadingStatusLink>
      );
    },
    header: I18n.t('total'),
    id: 'total',
  });

  const assignedItemsCols = useMemo(() => {
    const columns = assignedItems.map((assignmentModel) => {
      const accessorFn = (row: TableRow) => row
        .progresses
        .find(p => p.exercise_id === assignmentModel.id && p.exercise_type === assignmentModel.assignment_type);

      const header = getColumnName(assignmentModel);

      return (
        columnHelper.accessor(accessorFn, {
          cell: ({
            getValue, row: { original: { id, first_name, last_name } },
          }) => {
            const progress = getValue();

            return (
              <ReadingStatusLink
                cellId={`${assignmentModel.assignment_type}-${assignmentModel.id}`}
                progress={progress}
                sectionTitle={header}
                studentId={id}
                studentName={`${first_name} ${last_name}`}
              >
                <ReadingStatusIndicator progress={progress} />
                {progress && isQuestionScoringMode ? (
                  <span className="-tw-mt-0.5 tw-text-tci-black">
                    {typeof progress?.points === 'number' ? normalizedScore(assignment, progress.points) : '-'}
                  </span>
                ) : null}
              </ReadingStatusLink>
            );
          },
          header,
          id: `${assignmentModel.assignment_type}-${assignmentModel.id}`,
        })
      );
    });

    while (columns.length < 6) {
      columns.push(
        columnHelper.display({
          cell: () => <div className="tw-text-center">-</div>,
          header: '',
          id: `empty-header-${columns.length}`,
        }),
      );
    }

    return columns;
  }, [assignedItems]);

  return useReactTable({
    columns: [fullNameCol, totalCol, ...assignedItemsCols],
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
  });
}
